import React, { useEffect, useState } from 'react';
import { AlignLeft, Check, CheckSquare, List, Lock } from 'lucide-react';
import type { AccountPlan } from '../../global';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { cn } from '../../components/ui/cn';
import { useLanguage } from '../../lib/i18n';
import { isTemplateUnlocked, summaryTemplates, type SummaryTemplate, type TemplateSectionKind } from './summaryTemplates';
import { planName } from './plans';

interface TemplatePickerDialogProps {
  open: boolean;
  /** Şu an seçili şablon. */
  templateId: string;
  /** Hesabın planı — üst planlara özel şablonlar kilitli görünüyor. */
  plan: AccountPlan;
  onClose: () => void;
  onSelect: (templateId: string) => void;
}

const SECTION_ICON: Record<TemplateSectionKind, typeof AlignLeft> = {
  paragraph: AlignLeft,
  checklist: CheckSquare,
  outline: List,
};

/** Şablonun örnek çıktısı — solda seçilen şablon sağda böyle görünüyor. */
const TemplatePreview: React.FC<{ template: SummaryTemplate }> = ({ template }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <h3 className="text-xl font-bold tracking-tight">{template.name}</h3>
    <div className="mt-4 space-y-5">
      {template.sections.map((section) => {
        const Icon = SECTION_ICON[section.kind];
        return (
          <div key={section.title} className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="size-4 text-muted-foreground" />
              <h4 className="font-semibold">{section.title}</h4>
            </div>
            {section.kind === 'paragraph' ? (
              section.lines.map((line) => (
                <p key={line} className="text-sm leading-relaxed text-muted-foreground">
                  {line}
                </p>
              ))
            ) : (
              <ul className="space-y-1.5">
                {section.lines.map((line) => (
                  <li key={line} className="flex items-start gap-2 text-sm text-muted-foreground">
                    {section.kind === 'checklist' ? (
                      <span className="mt-0.5 size-3.5 shrink-0 rounded-[3px] border border-input" />
                    ) : (
                      <span className="mt-[0.45rem] size-1 shrink-0 rounded-full bg-muted-foreground" />
                    )}
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

/** Ayarlar > Özet stili > "Şablon seç" penceresi. */
export const TemplatePickerDialog: React.FC<TemplatePickerDialogProps> = ({
  open,
  templateId,
  plan,
  onClose,
  onSelect,
}) => {
  const { t } = useLanguage();
  const templates = summaryTemplates();
  const [previewId, setPreviewId] = useState(templateId);

  // Pencere açılırken seçili şablondan başla.
  useEffect(() => {
    if (open) setPreviewId(templateId);
  }, [open, templateId]);

  const preview = templates.find((template) => template.id === previewId) ?? templates[0];
  const unlocked = isTemplateUnlocked(preview, plan);
  const available = templates.filter((template) => isTemplateUnlocked(template, plan));
  const locked = templates.filter((template) => !isTemplateUnlocked(template, plan));

  const renderRow = (template: SummaryTemplate) => {
    const isPreviewing = template.id === previewId;
    const isSelected = template.id === templateId;
    const isLocked = !isTemplateUnlocked(template, plan);
    return (
      <button
        key={template.id}
        type="button"
        onClick={() => setPreviewId(template.id)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
          isPreviewing ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/60'
        )}
      >
        <span className="truncate">{template.name}</span>
        {isLocked ? (
          <Lock className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          isSelected && <Check className="size-4 shrink-0 text-brand" />
        )}
      </button>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Şablonlar', 'Templates')}
      className="max-w-3xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Vazgeç', 'Cancel')}
          </Button>
          <Button
            disabled={!unlocked}
            onClick={() => {
              onSelect(preview.id);
              onClose();
            }}
          >
            {unlocked
              ? t('Bu şablonu kullan', 'Use template')
              : preview.minPlan === 'pro'
                ? t('Ücretli plan gerekiyor', 'Requires a paid plan')
                : t(`${planName(preview.minPlan)} gerekiyor`, `Requires ${planName(preview.minPlan)}`)}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-[13rem_1fr]">
        {/* Sol: şablon listesi */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('Kullanılabilir şablonlar', 'Available templates')}
            </p>
            {available.map(renderRow)}
          </div>

          {locked.length > 0 && (
            <div className="space-y-1">
              <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('Üst planlarda', 'Upgrade for more')}
              </p>
              {locked.map(renderRow)}
            </div>
          )}
        </div>

        {/* Sağ: önizleme */}
        <div className="space-y-2">
          <TemplatePreview template={preview} />
          <p className="text-[0.8rem] text-muted-foreground">
            {unlocked
              ? t(
                  'Örnek çıktı. Gerçek özet toplantının kendi içeriğinden üretilir.',
                  'Sample output. The real summary is generated from your own meeting.'
                )
              : preview.minPlan === 'pro'
                ? t('Bu şablon ücretli planlarla açılıyor.', 'This template unlocks with any paid plan.')
                : t(
                    `Bu şablon ${planName(preview.minPlan)} planıyla açılıyor.`,
                    `This template unlocks with the ${planName(preview.minPlan)} plan.`
                  )}
          </p>
        </div>
      </div>
    </Dialog>
  );
};
